import { BackNavigationButton } from "$components/button/back-button";
import { Button } from "$components/button/button";
import { LoadingLogo } from "$components/loading/loading-logo";
import { PageTitle } from "$components/page";
import PageContent from "$components/page/page-content";
import { useAnalytics } from "$global-providers/analytics-provider/provider";
import {
  type InvestigationActivity,
  useRxCollectionInvestigationActivity,
} from "$global-providers/rxdb-provider/collections/investigation-activity";
import {
  RXDB_INVESTIGATION_CATALOGUE_ITEM_COLLECTION_NAME,
  type InvestigationCatalogueItem as RxInvestigationCatalogueItem,
} from "$global-providers/rxdb-provider/collections/investigation-catalogue-item";
import { usePatient } from "$global-providers/rxdb-provider/fallback-hooks/patient";
import { useRxDBContext } from "$global-providers/rxdb-provider/provider";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { type UserContextUser, useUser } from "$global-providers/user-provider";
import { DataAccessConsentModal } from "$routes/_lib/data-access-consent-modal";
import { linkToHome } from "$routes/_lib/links";
import NavigateHome from "$routes/_lib/navigate-home";
import { randomId } from "$routes/_lib/random-id";
import useFacility, { type UserFacility } from "$routes/_lib/use-facility";
import { useQuerystring } from "$routes/_lib/use-querystring";
import ValidationContext from "$routes/_lib/validation/validation-context";
import { usePreServiceBilling } from "$routes/billing/_lib/pre-service/use-pre-service-billing";
import { useDefaultValues } from "$routes/patient/[:patientId]/_lib/investigations/new-investigations/default-values";
import {
  L10N,
  translateI18nObject,
} from "$routes/patient/[:patientId]/_lib/translation-util";
import { InvestigationRecommendationLocation } from "$routes/patient/[:patientId]/consultation/new/page";
import { AddInvestigationCard } from "$routes/patient/[:patientId]/investigations/_lib/add-investigation-card";
import { InvestigationCardField } from "$routes/patient/[:patientId]/investigations/_lib/investigation-card-field";
import { InvestigationSelect } from "$routes/patient/[:patientId]/investigations/_lib/investigation-select";
import { populateOrderedSchema } from "$routes/patient/[:patientId]/investigations/_lib/populate-ordered-schema";
import {
  generateBlankInvestigation,
  generatePopulatedInvestigation,
  generateUpdatedInvestigation,
  generateUpdatedInvestigationFromRecommendation,
  type TranslatedInvestigationCatalogueItem,
} from "$routes/patient/[:patientId]/investigations/_lib/util";
import { linkToPatientDetails } from "$routes/patient/[:patientId]/links";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, styled } from "@material-ui/core";
import {
  type CatalogueCoding,
  type ClinicalEventType,
  PriceableItemOrigin,
} from "elephant-web/gql";
import type { JSONSchema7TypeName } from "json-schema";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import { v4 } from "uuid";
import AddInvestigationButton from "../../_lib/add-investigation-button";
import { getRemainingRecommendations } from "../../_lib/clinical-decision-support-service/mappers";
import { useClinicalDecisionSupportService } from "../../_lib/clinical-decision-support-service/provider";
import {
  addRelevantFormInvestigationsToCDS,
  removeRelevantFormInvestigationsFromCDS,
} from "../../_lib/form-investigation-to-cds";
import { type FormValues, useValidationSchema } from "./validation-schema";

const Form = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export const NewInvestigationPage = () => {
  const [trackingId] = useState(v4());
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const query = useQuerystring();
  const uuid = query.patientUuid;
  const facility = useFacility().data;
  const user = useUser();
  const { getCurrentTime } = useServerTime();
  const investigationActivityCollection =
    useRxCollectionInvestigationActivity();
  const { trackEvent } = useAnalytics();
  const { emit: emitPreServiceLineItem } = usePreServiceBilling();

  const {
    replicationState: {
      investigationCatalogueItem: { isPulling: isCatalogueItemsPulling },
    },
  } = useRxDBContext();
  const { result: catalogueItemsResult, isFetching: catalogueItemsFetching } =
    useRxData<RxInvestigationCatalogueItem>(
      RXDB_INVESTIGATION_CATALOGUE_ITEM_COLLECTION_NAME,
      (collection) => collection.find(),
    );
  const catalogueItems = useMemo(
    () => catalogueItemsResult.map((item) => item._data),
    [catalogueItemsResult],
  );

  // Used by the vitals and allergies pages to pre-select their investigations
  const { investigationLoinc } = useQuerystring();

  const {
    recommendations,
    patientClinicalResources: {
      submit: submitClinicalResources,
      add: addClinicalResource,
      remove: removeClinicalResource,
    },
  } = useClinicalDecisionSupportService();

  useEffect(() => {
    for (const [index, recommendation] of recommendations.entries()) {
      trackEvent("Investigation RecommendationShown", {
        trackingId,
        patientId: uuid,
        recommendationId: recommendation.id,
        recommendationDiseaseName: recommendation.diseaseDisplayName,
        recommendationInvestigationLoincCodes: recommendation.investigations
          .map((investigation) => investigation.catalogueItemLoincCode)
          .join(", "),
        recommendationPosition: index + 1,
      });
    }
  }, [recommendations, trackEvent, trackingId, uuid]);

  const itemsLoading =
    isCatalogueItemsPulling ||
    catalogueItemsFetching ||
    catalogueItems.length === 0;

  const translatedInvestigationList = useMemo(() => {
    return (catalogueItems?.map((investigation) =>
      translateI18nObject(investigation, L10N.EN),
    ) ?? []) as unknown as Array<TranslatedInvestigationCatalogueItem>;
  }, [catalogueItems]);

  const validationSchema = useValidationSchema();
  const defaultValues = useDefaultValues();

  const formMethods = useForm<FormValues>({
    mode: "onTouched",
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const {
    append: appendInvestigation,
    fields: formInvestigations,
    remove: removeInvestigation,
    update: updateInvestigation,
  } = useFieldArray({
    name: "investigations",
    control: formMethods.control,
  });

  const recommendedInvestigations = useMemo(() => {
    return getRemainingRecommendations({
      investigationCatalogueLoincCodes: formInvestigations
        .map((i) => i.catalogueItem?.coding?.loinc?.code)
        .filter(Boolean),
      recommendations,
    });
  }, [recommendations, formInvestigations]);

  const handleAddBlankInvestigation = useCallback(() => {
    appendInvestigation(generateBlankInvestigation());
  }, [appendInvestigation]);

  useEffect(() => {
    if (!investigationLoinc || formInvestigations.length > 0) return;
    const investigation = translatedInvestigationList?.find(
      ({ coding }) => coding?.loinc?.code === investigationLoinc,
    );
    if (investigation) {
      appendInvestigation(generatePopulatedInvestigation(investigation), {
        shouldFocus: false,
      });
    }
  }, [
    investigationLoinc,
    appendInvestigation,
    translatedInvestigationList,
    formInvestigations,
  ]);

  // Initialize with a placeholder if there are no items
  useEffect(() => {
    if (formInvestigations.length === 0 && !investigationLoinc) {
      handleAddBlankInvestigation();
    }
  }, [
    handleAddBlankInvestigation,
    investigationLoinc,
    formInvestigations.length,
  ]);

  const submitForm = useCallback(
    async (values: FormValues) => {
      if (facility) {
        const investigationActivities =
          mapFormValuesToRxInvestigationActivities({
            formValues: {
              investigations: values.investigations.map((item) => ({
                ...item,
                catalogueItem: item.catalogueItem,
                key: item.id,
              })),
            },
            getCurrentTime,
            patientId: uuid,
            user,
            facility,
            investigationList: catalogueItems,
          });

        await investigationActivityCollection?.bulkUpsert(
          investigationActivities,
        );

        try {
          for (const [i, inv] of values.investigations.entries()) {
            const catalogueItemId = inv.catalogueItem?.id;
            if (!catalogueItemId) continue;
            await emitPreServiceLineItem({
              patientId: uuid,
              externalReference: {
                origin: PriceableItemOrigin.Investigation,
                foreignId: catalogueItemId,
              },
              scopeId: `${trackingId}:inv-${i}`,
              actorId: user.id,
            });
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(
            "PRE_SERVICE line-item emit failed (standalone investigations)",
            err,
          );
        }

        await submitClinicalResources();
        navigate(linkToPatientDetails({ patientUuid: uuid }));
      }
      return;
    },
    [
      uuid,
      user,
      getCurrentTime,
      catalogueItems,
      investigationActivityCollection?.bulkUpsert,
      facility,
      navigate,
      submitClinicalResources,
      emitPreServiceLineItem,
      trackingId,
    ],
  );

  const { data: patient, loading: patientLoading } = usePatient({
    id: uuid,
  });

  useEffect(() => {
    if (!(patientLoading || patient)) {
      toast.error(
        formatMessage({
          id: "patientDetails.noPatientFound.error",
          defaultMessage:
            "There was an error loading patient details: Patient not found",
        }),
      );
      navigate(linkToHome());
    }
  }, [patientLoading, patient, formatMessage, navigate]);

  if (!uuid) {
    return <NavigateHome />;
  }

  if (patientLoading) {
    return <LoadingLogo />;
  }

  return (
    <div style={{ width: "100%", padding: "8px 16px 0" }}>
      <BackNavigationButton
        to={linkToPatientDetails({ patientUuid: uuid })}
        label={{
          id: "inpatient.admit.backToPatientOverview",
          defaultMessage: "Back to Patient Overview",
        }}
      />
      <PageTitle>
        <FormattedMessage
          id="investigations.new.title"
          defaultMessage="Add Investigations"
        />
      </PageTitle>
      <PageContent>
        <ValidationContext.Provider value={{ validationSchema }}>
          <FormProvider {...formMethods}>
            <Form onSubmit={formMethods.handleSubmit(submitForm)}>
              {formInvestigations.map((item, index) => {
                return (
                  <AddInvestigationCard
                    key={item.id}
                    hasSelectedInvestigation={!!item?.catalogueItem}
                    recommendedInvestigations={recommendedInvestigations.flatMap(
                      (recommendation) => recommendation.investigations,
                    )}
                    onRecommendedClick={(value) => {
                      const investigation =
                        generateUpdatedInvestigationFromRecommendation({
                          recommendedInvestigation: value,
                          translatedInvestigationList,
                          formInvestigation: formInvestigations[index],
                        });
                      updateInvestigation(index, investigation);
                      trackEvent("Investigation RecommendationSelected", {
                        recommendationInvestigation: value.displayName,
                        recommendationCatalogueItemLoincCode:
                          value.catalogueItemLoincCode,
                        recommendationCode: value.code,
                        recommendationLocation:
                          InvestigationRecommendationLocation.INVESTIGATION_CARD,
                        patientId: uuid,
                        recommendationIndex: index + 1,
                        trackingId,
                      });
                    }}
                    onDelete={() => {
                      removeInvestigation(index);
                      if (item?.catalogueItem) {
                        removeRelevantFormInvestigationsFromCDS({
                          formItem: item,
                          removeItem: removeClinicalResource,
                        });
                      }
                    }}
                  >
                    <InvestigationSelect
                      investigationList={translatedInvestigationList}
                      error={
                        formMethods.formState.errors.investigations?.[index]
                          ?.catalogueItem?.message ?? null
                      }
                      onChange={(value) => {
                        const item = formInvestigations[index];
                        if (
                          item?.catalogueItem &&
                          item?.catalogueItem.id !== value.id
                        ) {
                          removeRelevantFormInvestigationsFromCDS({
                            formItem: item,
                            removeItem: removeClinicalResource,
                          });
                        }
                        if (value) {
                          updateInvestigation(
                            index,
                            generateUpdatedInvestigation({
                              formInvestigation: formInvestigations[index],
                              catalogueItem: value,
                            }),
                          );
                        } else {
                          updateInvestigation(
                            index,
                            generateBlankInvestigation(
                              formInvestigations[index].id,
                            ),
                          );
                        }
                        formMethods.trigger();
                      }}
                      value={formInvestigations[index]?.catalogueItem || null}
                      loading={itemsLoading}
                      onClear={() => {
                        const item = formInvestigations[index];
                        if (item?.catalogueItem) {
                          removeRelevantFormInvestigationsFromCDS({
                            formItem: item,
                            removeItem: removeClinicalResource,
                          });
                          updateInvestigation(
                            index,
                            generateBlankInvestigation(
                              formInvestigations[index].id,
                            ),
                          );
                        }
                      }}
                    />
                    {!!item?.catalogueItem && (
                      <>
                        <Divider />
                        <InvestigationCardField
                          catalogueItem={item.catalogueItem}
                          key={item.id}
                          index={index}
                          onDelete={() => {
                            removeInvestigation(index);
                          }}
                          fieldNames={{ investigations: "investigations" }}
                          onInvestigationChanged={(formItem) => {
                            addRelevantFormInvestigationsToCDS({
                              formItem,
                              addClinicalResource,
                              userId: user.id,
                            });
                          }}
                        />
                      </>
                    )}
                  </AddInvestigationCard>
                );
              })}

              <Box
                display="flex"
                justifyContent="flex-end"
                gridGap={"10px"}
                mb={2}
              >
                <AddInvestigationButton
                  onClick={() => handleAddBlankInvestigation()}
                />
                <Button
                  data-testid="submitNewInvestigation"
                  variant="contained"
                  color="primary"
                  type="submit"
                >
                  <FormattedMessage
                    id="investigations.new.submit"
                    defaultMessage="Submit"
                  />
                </Button>
              </Box>
            </Form>
          </FormProvider>
        </ValidationContext.Provider>
      </PageContent>
      <DataAccessConsentModal patientId={uuid} />
    </div>
  );
};

export const mapFormValuesToRxInvestigationActivities = ({
  formValues,
  investigationList,
  patientId,
  user,
  facility,
  getCurrentTime,
  clinicalEventId,
  clinicalEventType,
  generateId = randomId,
}: {
  formValues: FormValues;
  investigationList: RxInvestigationCatalogueItem[];
  patientId: string;
  user: UserContextUser;
  facility: UserFacility;
  getCurrentTime: () => Date;
  clinicalEventId?: string;
  clinicalEventType?: ClinicalEventType;
  generateId?: () => string;
}): InvestigationActivity[] => {
  const performer = {
    id: user.id,
    name: `${user.firstName} ${user.lastName}`,
  };
  const organisation = {
    id: user.organisationId,
    name: facility.organisation,
  };
  const clinic = {
    id: user.facilityId,
    name: facility.name,
  };
  const currentTime = getCurrentTime().toISOString();
  const meta = {
    createdAt: currentTime,
    updatedAt: currentTime,
    createdByUserId: user.id,
    updatedByUserId: user.id,
    deleted: false,
  };

  type ResultSchema = {
    type: JSONSchema7TypeName;
    orderedProperties: Record<string, any>[];
    title: string;
    investigationMeta: {
      coding?: CatalogueCoding;
    };
  };

  return formValues.investigations.map(
    ({ catalogueItem, isRequested, outcome, id }) => {
      // TODO: Use a more efficient way to find the original item
      const originalCatalogueItem = investigationList.find(
        (inv) => inv.id === catalogueItem.id,
      );

      const requestAndOutcomeSharedData = {
        performer,
        organisation,
        clinic,
        meta: {
          created: meta.createdAt,
          updated: meta.updatedAt,
        },
      };

      const populatedOutcomeSchema = populateOrderedSchema(
        outcome,
        originalCatalogueItem.result.resultSchema,
      );

      const formattedRxdbInvestigation = {
        id,
        catalogueItem: {
          id: catalogueItem.id,
          title: { en: catalogueItem.title },
          result: catalogueItem.result ? catalogueItem.result : null,
          activityType: catalogueItem.activityType,
          meta: catalogueItem.meta,
          coding: catalogueItem.coding
            ? {
                elephant: catalogueItem.coding.elephant
                  ? {
                      code: catalogueItem.coding.elephant.code,
                      system: catalogueItem.coding.elephant.system ?? null,
                    }
                  : null,
                loinc: catalogueItem.coding.loinc
                  ? {
                      code: catalogueItem.coding.loinc.code,
                      system: catalogueItem.coding.loinc.system ?? null,
                    }
                  : null,
              }
            : null,
          version: catalogueItem.version,
          keywords: catalogueItem.keywords
            ? catalogueItem.keywords.map((kw) => ({ en: kw }))
            : null,
        },
        clinicalEvent:
          clinicalEventId && clinicalEventType
            ? {
                id: clinicalEventId,
                type: clinicalEventType,
              }
            : null,
        meta,
        patientId,
        outcome: isRequested
          ? null
          : Object.assign(
              {
                id: generateId(),
                orderedProperties: populatedOutcomeSchema.orderedProperties,
              },
              requestAndOutcomeSharedData,
            ),
        request: Object.assign(
          {
            id: generateId(),
            orderedProperties: (
              originalCatalogueItem.result.resultSchema as ResultSchema
            ).orderedProperties,
          },
          requestAndOutcomeSharedData,
        ),
      };
      return formattedRxdbInvestigation;
    },
  );
};
